import Modal from "@via/components/modal";
import { ModalSize, ROUTES } from "@via/utils/constants";
import { GenericError } from "@via/components/error";
import { SkeletonLoader } from "@via/components/loading/loading";
import { EmptyDataIcon } from "@via/utils/custom-icons";
import { useEffect, useMemo, useState, type JSX } from "react";
import SelectComponent from "@via/components/select/select";
import type { SelectOption } from "@via/utils/interfaces";
import type {
  RunSchedulerSourceState,
  RunSchedulerSourceStateRow,
} from "common";
import { useSchedulerSourcesByState } from "@via/pages/runs/schedulers/schedulers.api";
import { formatDateTime } from "@via/pages/account/admin/notification-settings/utils/date-helpers";
import { Link } from "react-router-dom";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  schedulerId: string | number | null;
  schedulerName?: string;
  initialState: RunSchedulerSourceState;
};

const STATE_OPTIONS: Array<{ label: string; value: RunSchedulerSourceState }> =
  [
    { label: "Submitted", value: "submitted" },
    { label: "Pending", value: "pending" },
    { label: "Errors", value: "error" },
  ];

function formatRunLink(runId: number): string {
  return ROUTES.RUN_DETAILS.replace(":runId", String(runId));
}

function renderReason(row: RunSchedulerSourceStateRow): string {
  if (row.state === "submitted") return "-";
  return row.reason ?? "-";
}

export default function SchedulerSourcesModal({
  open,
  setOpen,
  schedulerId,
  schedulerName,
  initialState,
}: Props): JSX.Element {
  const [state, setState] = useState<RunSchedulerSourceState>(initialState);
  const [take] = useState<number>(50);
  const [skip, setSkip] = useState<number>(0);

  useEffect(() => {
    if (!open) return;
    setState(initialState);
    setSkip(0);
  }, [open, initialState]);

  const { data, isLoading, isError } = useSchedulerSourcesByState(
    schedulerId,
    state,
    take,
    skip,
    open
  );

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;

  const canPrev = skip > 0;
  const canNext = skip + take < total;

  const stateOption = useMemo<SelectOption | null>(() => {
    const opt = STATE_OPTIONS.find(o => o.value === state);
    return opt ? { label: opt.label, value: opt.value } : null;
  }, [state]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={schedulerName ? `Sources • ${schedulerName}` : "Scheduler sources"}
      size={ModalSize.XLarge}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-900">
              Status
            </label>
            <div className="mt-2">
              <SelectComponent
                name="scheduler-sources-state"
                value={stateOption}
                onChange={selected => {
                  const next = Array.isArray(selected)
                    ? ((selected[0] as SelectOption | undefined) ?? null)
                    : (selected as SelectOption | null);
                  const nextState =
                    (next?.value as RunSchedulerSourceState) ?? "submitted";
                  setState(nextState);
                  setSkip(0);
                }}
                options={STATE_OPTIONS.map(o => ({
                  label: o.label,
                  value: o.value,
                }))}
                placeholder="Select a state"
                readOnly={isLoading}
              />
            </div>
          </div>

          <div className="flex items-end justify-between gap-2">
            <div className="text-sm text-gray-700">
              {isLoading ? (
                <span className="text-gray-400">Loading…</span>
              ) : (
                <span>
                  Showing{" "}
                  <span className="font-medium">
                    {total === 0 ? 0 : skip + 1}-{Math.min(skip + take, total)}
                  </span>{" "}
                  of <span className="font-medium">{total}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-3">
          {isLoading ? (
            <SkeletonLoader count={8} />
          ) : isError ? (
            <div className="via-generic-error">
              <GenericError message="Failed to load sources." />
            </div>
          ) : rows.length === 0 ? (
            <div className="via-generic-error">
              <EmptyDataIcon />
              <p>No sources in this state.</p>
            </div>
          ) : (
            <div className="max-h-[480px] overflow-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-900">
                      Source
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-900">
                      Run
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-900">
                      Reason
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-900">
                      Last changed
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td className="px-3 py-2 text-gray-900">
                        <div
                          className="max-w-[520px] truncate"
                          title={r.sourcePath ?? ""}>
                          {r.sourcePath ?? "-"}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {r.runId ? (
                          <Link
                            className="text-indigo-600 hover:text-indigo-500"
                            target="_blank"
                            rel="noreferrer"
                            to={formatRunLink(r.runId)}>
                            {r.runId}
                          </Link>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        <div
                          className="max-w-[240px] truncate"
                          title={renderReason(r)}>
                          {renderReason(r)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {formatDateTime(r.lastStateChangedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={!canPrev || isLoading}
            onClick={() => setSkip(s => Math.max(s - take, 0))}
            className="inline-flex items-center justify-center rounded-md bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
            Prev
          </button>
          <button
            type="button"
            disabled={!canNext || isLoading}
            onClick={() => setSkip(s => s + take)}
            className="inline-flex items-center justify-center rounded-md bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
            Next
          </button>
        </div>
      </div>
    </Modal>
  );
}
